(function () {
  /*
   * ================================================================
   * Custom complementary products
   * ================================================================
   *
   * Responsibilities:
   * 1. Add selected recommended products before the main product is added.
   * 2. Show/hide the free-shipping unlock notice.
   * 3. Use Recharge subscription price when a subscription option is selected.
   * 4. Recalculate after cart mutations, variant/quantity changes, Recharge changes,
   *    and delayed recommendation rendering.
   */

  /* ----------------------------------------------------------------
   * State and selectors
   * ---------------------------------------------------------------- */

  let isAddingComplementaryProducts = false;
  let freeShippingNoticeRun = 0;
  let mainVariantPriceMap = null;
  let customCompNoticeRecheckTimer = null;
  const addedComplementaryVariantIds = new Set();

  /* ----------------------------------------------------------------
   * Temporary diagnostic logging (duplicate add-to-cart investigation)
   * Set window.__CUSTOM_COMP_DEBUG__ = false in console to silence.
   * ---------------------------------------------------------------- */

  let customCompCartMutationCounter = 0;

  function debugLog(label, detail) {
    if (window.__CUSTOM_COMP_DEBUG__ === false) return;

    console.log(
      '%c[custom comp DEBUG] ' + label,
      'color:#8a0016;font-weight:bold;',
      detail || {}
    );
  }

  const SELECTORS = {
    wrapper: '[data-aid="custom-complementary-products-block"]',
    notice: '.custom-comp-free-shipping-notice',
    toggle: '.js-custom-comp-toggle',
    productForm: '#add-to-cart-form, form[action*="/cart/add"]',
    mainVariantPricesJson: '[data-custom-comp-main-variant-prices]'
  };

  /* ----------------------------------------------------------------
   * Shopify route and DOM helpers
   * ---------------------------------------------------------------- */

  function getShopifyRoute(path) {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

    return root + path.replace(/^\//, '');
  }

  function getWrapper() {
    return document.querySelector(SELECTORS.wrapper);
  }

  function getNotice() {
    const wrapper = getWrapper();
    return wrapper ? wrapper.querySelector(SELECTORS.notice) : null;
  }

  function setNoticeVisible(isVisible) {
    const notice = getNotice();

    if (!notice) return;

    notice.hidden = !isVisible;
    notice.classList.toggle('is-visible', isVisible);
    notice.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
  }

  function queryAllDeep(selector, root = document) {
    const results = [];

    if (root.querySelectorAll) {
      results.push(...root.querySelectorAll(selector));
    }

    if (root.querySelectorAll) {
      root.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) {
          results.push(...queryAllDeep(selector, element.shadowRoot));
        }
      });
    }

    return results;
  }

  /* ----------------------------------------------------------------
   * Main product form, variant, and quantity helpers
   * ---------------------------------------------------------------- */

  function getMainVariantPriceMap() {
    if (mainVariantPriceMap !== null) return mainVariantPriceMap;

    mainVariantPriceMap = {};

    const script = document.querySelector(SELECTORS.mainVariantPricesJson);

    if (!script) return mainVariantPriceMap;

    try {
      mainVariantPriceMap = JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('[custom comp] could not parse main variant prices JSON:', error);
      mainVariantPriceMap = {};
    }

    return mainVariantPriceMap;
  }

  function getProductForm() {
    const priceMap = getMainVariantPriceMap();

    const forms = Array.from(
      document.querySelectorAll('#add-to-cart-form, form[action*="/cart/add"]')
    );

    const matchingMainProductForm = forms.find((form) => {
      const variantInput = form.querySelector('[name="id"]');
      const variantId = variantInput && variantInput.value;

      return variantId && Object.prototype.hasOwnProperty.call(priceMap, variantId);
    });

    if (matchingMainProductForm) {
      return matchingMainProductForm;
    }

    const mainAddButton = document.querySelector(
      '.product-page--submit-action button, .product-page--submit-action input[type="submit"]'
    );

    if (mainAddButton) {
      const mainForm = mainAddButton.closest('form');

      if (mainForm) {
        return mainForm;
      }
    }

    return forms[0] || null;
  }

  function getSelectedMainVariantId() {
    const form = getProductForm();
    const variantInput = form && form.querySelector('[name="id"]');

    return variantInput ? variantInput.value : null;
  }

  function isMainVariantSoldOut() {
    // Prefer explicit sold-out UI from the theme purchase button.
    const purchaseButton = document.querySelector(
      '.product-page--submit-action #purchase, #purchase, .product-page--submit-action [name="add"], [name="add"]'
    );

    if (purchaseButton) {
      const label = String(purchaseButton.textContent || purchaseButton.value || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();

      if (
        purchaseButton.disabled &&
        (label.includes('sold out') || label.includes('unavailable'))
      ) {
        return true;
      }
    }

    // Fallback: product JSON + selected variant id / checked sold-out swatch.
    if (document.querySelector('#add-to-cart-form .swatch-element.soldout input:checked')) {
      return true;
    }

    const variantId = getSelectedMainVariantId();

    if (!variantId) {
      return false;
    }

    const scripts = document.querySelectorAll('script[type="application/json"]');

    for (const script of scripts) {
      try {
        const data = JSON.parse(script.textContent || '');

        if (!data || !Array.isArray(data.variants) || !data.title) {
          continue;
        }

        const variant = data.variants.find(function (entry) {
          return String(entry.id) === String(variantId);
        });

        if (variant) {
          return variant.available === false;
        }
      } catch (error) {
        // Ignore unrelated JSON blobs.
      }
    }

    return false;
  }

  function getMainProductQuantity() {
    const form = getProductForm();
    const quantityInput = form && form.querySelector('[name="quantity"]');
    const quantity = Number(quantityInput && quantityInput.value);

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  /* ----------------------------------------------------------------
   * Complementary product selection helpers
   * ---------------------------------------------------------------- */

  function getSelectedComplementaryItems() {
    return Array.from(document.querySelectorAll(`${SELECTORS.toggle}:checked`))
      .map((input) => {
        const variantId = Number(input.dataset.variantId);
        const variantPrice = Number(input.dataset.variantPrice) || 0;

        // Already added this variant in this page session — do not add it
        // again just because a stale/re-rendered switch is still checked.
        if (!variantId || addedComplementaryVariantIds.has(variantId)) return null;

        return {
          id: variantId,
          quantity: 1,
          price: variantPrice
        };
      })
      .filter(Boolean);
  }

  function markComplementaryItemsAsAdded(items) {
    items.forEach((item) => {
      addedComplementaryVariantIds.add(item.id);
    });

    document.querySelectorAll(SELECTORS.toggle).forEach((toggle) => {
      const variantId = Number(toggle.dataset.variantId);

      if (!addedComplementaryVariantIds.has(variantId)) return;

      if (toggle.checked) {
        toggle.checked = false;
      }

      const card = toggle.closest('.complementary-product-card-wrapper');

      if (card) {
        card.classList.add('custom-comp-card--already-added');
      }
    });
  }

  function getRecommendedComplementaryProductPriceCents() {
    const toggle = document.querySelector(SELECTORS.toggle);

    return Number(toggle && toggle.dataset.variantPrice) || 0;
  }

  /* ----------------------------------------------------------------
   * Money parsing and visible text helpers
   * ---------------------------------------------------------------- */

  function parseMoneyTextToCents(text) {
    const rawText = String(text || '');

    const numberMatches = rawText.match(/\d{1,3}(?:[.,]\d{3})*(?:[.,]\d+)?|\d+/g);

    if (!numberMatches || !numberMatches.length) {
      return 0;
    }

    let value = numberMatches[numberMatches.length - 1];

    const lastComma = value.lastIndexOf(',');
    const lastDot = value.lastIndexOf('.');

    if (lastComma > -1 && lastDot > -1) {
      if (lastComma > lastDot) {
        value = value.replace(/\./g, '').replace(',', '.');
      } else {
        value = value.replace(/,/g, '');
      }
    } else if (lastComma > -1) {
      value = value.replace(',', '.');
    }

    const number = Number.parseFloat(value);

    return Number.isFinite(number) ? Math.round(number * 100) : 0;
  }

  function getVisiblePriceText(element) {
    if (!element) {
      return '';
    }

    const clone = element.cloneNode(true);

    clone.querySelectorAll(
      '.visually-hidden, [aria-hidden="true"], [hidden]'
    ).forEach((hiddenElement) => {
      hiddenElement.remove();
    });

    return clone.textContent || '';
  }

  /* ----------------------------------------------------------------
   * Recharge subscription price helpers
   * ---------------------------------------------------------------- */

  function isSubscriptionPurchaseSelected() {
    const form = getProductForm();
    const sellingPlanInput = form && form.querySelector('[name="selling_plan"]');

    return !!(sellingPlanInput && sellingPlanInput.value);
  }

  function getRechargeSubscriptionPriceCents() {
    if (!isSubscriptionPurchaseSelected()) {
      return 0;
    }

    const priceElements = queryAllDeep(
      '[part="rc-purchase-option__discounted-price"], .rc-price[part*="discounted-price"]'
    );

    const priceElement = priceElements.find((element) => {
      const visibleText = getVisiblePriceText(element);

      return element.getClientRects().length > 0 &&
        parseMoneyTextToCents(visibleText) > 0;
    });

    return priceElement
      ? parseMoneyTextToCents(getVisiblePriceText(priceElement))
      : 0;
  }

  /* ----------------------------------------------------------------
   * Main product price calculation
   * ---------------------------------------------------------------- */

  function getNormalMainVariantPriceCents() {
    const variantId = getSelectedMainVariantId();
    const priceMap = getMainVariantPriceMap();

    return Number(priceMap[variantId]) || 0;
  }

  function getMainProductUnitPriceCents() {
    const rechargeSubscriptionPriceCents = getRechargeSubscriptionPriceCents();

    if (rechargeSubscriptionPriceCents) {
      return rechargeSubscriptionPriceCents;
    }

    return getNormalMainVariantPriceCents();
  }

  function getMainProductTotalCents() {
    return getMainProductUnitPriceCents() * getMainProductQuantity();
  }

  /* ----------------------------------------------------------------
   * Cart total and free-shipping notice logic
   * ---------------------------------------------------------------- */

  function getFreeShippingThresholdCents() {
    const wrapper = getWrapper();
    return Number(wrapper && wrapper.dataset.freeShippingThresholdCents) || 0;
  }

  async function getCurrentCartTotalCents() {
    try {
      const cart = typeof window.getSharedCartJs === 'function'
        ? await window.getSharedCartJs({ reason: 'custom-comp notice' })
        : await (await fetch(getShopifyRoute('cart.js'), {
            headers: { Accept: 'application/json' }
          })).json();

      return Number(cart.total_price) || 0;
    } catch (error) {
      console.warn('[custom comp] could not fetch cart total; using initial Liquid cart total:', error);

      const wrapper = getWrapper();
      return Number(wrapper && wrapper.dataset.initialCartTotalCents) || 0;
    }
  }

  async function updateFreeShippingNotice() {
    const run = ++freeShippingNoticeRun;

    const thresholdCents = getFreeShippingThresholdCents();
    const recommendedProductPriceCents = getRecommendedComplementaryProductPriceCents();

    if (!thresholdCents || !recommendedProductPriceCents) {
      setNoticeVisible(false);
      return;
    }

    const mainProductTotalCents = getMainProductTotalCents();

    if (!mainProductTotalCents) {
      setNoticeVisible(false);
      return;
    }

    const currentCartTotalCents = await getCurrentCartTotalCents();

    if (run !== freeShippingNoticeRun) return;

    const totalWithoutRecommendedProduct =
      currentCartTotalCents + mainProductTotalCents;

    const totalWithRecommendedProduct =
      totalWithoutRecommendedProduct + recommendedProductPriceCents;

    const shouldShowNotice =
      totalWithoutRecommendedProduct < thresholdCents &&
      totalWithRecommendedProduct >= thresholdCents;

    setNoticeVisible(shouldShowNotice);
  }

  /* ----------------------------------------------------------------
   * Free-shipping notice recheck scheduling
   * ---------------------------------------------------------------- */

  function scheduleFreeShippingNoticeRecheck(delay = 500) {
    if (!getWrapper()) {
      return;
    }

    window.clearTimeout(customCompNoticeRecheckTimer);

    customCompNoticeRecheckTimer = window.setTimeout(function () {
      updateFreeShippingNotice();
    }, delay);
  }

  function installInitialRecommendedProductReadyRecheck() {
    const wrapper = getWrapper();

    if (!wrapper) {
      return;
    }

    if (window.__customCompInitialRecommendedProductReadyRecheckInstalled) {
      return;
    }

    window.__customCompInitialRecommendedProductReadyRecheckInstalled = true;

    function hasRecommendedProductPrice() {
      return getRecommendedComplementaryProductPriceCents() > 0;
    }

    if (hasRecommendedProductPrice()) {
      scheduleFreeShippingNoticeRecheck(100);
      scheduleFreeShippingNoticeRecheck(600);
      return;
    }

    const observer = new MutationObserver(function () {
      updateCustomCompBlockVisibility();

      if (!hasRecommendedProductPrice()) {
        return;
      }

      scheduleFreeShippingNoticeRecheck(100);
      scheduleFreeShippingNoticeRecheck(600);

      observer.disconnect();
    });

    observer.observe(wrapper, {
      childList: true,
      subtree: true
    });

    window.setTimeout(function () {
      observer.disconnect();
    }, 5000);

    scheduleFreeShippingNoticeRecheck(500);
    scheduleFreeShippingNoticeRecheck(1500);
    scheduleFreeShippingNoticeRecheck(3000);
  }

  function updateCustomCompBlockVisibility() {
  const wrapper = getWrapper();

  if (!wrapper) {
    return;
  }

  const hasProductCard = !!wrapper.querySelector('.complementary-product-card-wrapper');
  const hasToggle = !!wrapper.querySelector(SELECTORS.toggle);
  const soldOut = isMainVariantSoldOut();
  const shouldShow = hasProductCard && hasToggle && !soldOut;

  wrapper.hidden = !shouldShow;
  wrapper.classList.toggle('hidden', !shouldShow);
  wrapper.classList.toggle('is-main-variant-soldout', soldOut);
  document.documentElement.classList.toggle('custom-comp-main-soldout', soldOut);

  // Sold-out main variant: clear upsell selection so it cannot ride along later.
  if (soldOut) {
    wrapper.querySelectorAll(SELECTORS.toggle).forEach(function (toggle) {
      if (toggle.checked) {
        toggle.checked = false;
        toggle.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    setNoticeVisible(false);
  }
}


let customCompRecommendationsRefreshTimer = null;
let isRefreshingCustomCompRecommendations = false;

function getRecommendationsElement() {
  const wrapper = getWrapper();

  return wrapper
    ? wrapper.querySelector('[data-aid="custom-complementary-product-recommendations"]')
    : null;
}

async function refreshCustomCompRecommendations() {
  const recommendationsElement = getRecommendationsElement();
  const recommendationsUrl = recommendationsElement && recommendationsElement.dataset.url;

  if (!recommendationsElement || !recommendationsUrl || isRefreshingCustomCompRecommendations) {
    updateCustomCompBlockVisibility();
    return;
  }

  isRefreshingCustomCompRecommendations = true;

  try {
    const response = await fetch(recommendationsUrl, {
      headers: {
        Accept: 'text/html'
      }
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }

    const html = await response.text();
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');

    const freshRecommendationsElement = parsedDocument.querySelector(
      '[data-aid="custom-complementary-product-recommendations"]'
    );

    if (!freshRecommendationsElement) {
      updateCustomCompBlockVisibility();
      return;
    }

    recommendationsElement.innerHTML = freshRecommendationsElement.innerHTML;

    // Re-apply already-added state — the freshly-rendered markup has no
    // idea which variants were already added via this widget.
    markComplementaryItemsAsAdded([]);

    updateCustomCompBlockVisibility();
    scheduleFreeShippingNoticeRecheck(100);
    scheduleFreeShippingNoticeRecheck(600);
  } catch (error) {
    console.warn('[custom comp] could not refresh complementary recommendations:', error);
    updateCustomCompBlockVisibility();
  } finally {
    isRefreshingCustomCompRecommendations = false;
  }
}

function scheduleCustomCompRecommendationsRefresh(delay = 900) {
  if (!getWrapper()) {
    return;
  }

  window.clearTimeout(customCompRecommendationsRefreshTimer);

  customCompRecommendationsRefreshTimer = window.setTimeout(function () {
    refreshCustomCompRecommendations();
  }, delay);
}

  /* ----------------------------------------------------------------
   * Cart mutation recheck hooks
   * ---------------------------------------------------------------- */

  function isCartMutationUrl(url) {
    const normalizedUrl = String(url || '');

    return (
      normalizedUrl.includes('/cart/add') ||
      normalizedUrl.includes('/cart/change') ||
      normalizedUrl.includes('/cart/update') ||
      normalizedUrl.includes('/cart/clear')
    );
  }

  function getRequestUrl(input) {
    if (!input) {
      return '';
    }

    if (typeof input === 'string') {
      return input;
    }

    if (input.url) {
      return input.url;
    }

    return String(input);
  }

  // The "MWS Upsell Bundle" app (mws_upsell_bundle.js) independently replays
  // /cart/add(.js) requests — sometimes as a bare {id, quantity} fetch,
  // sometimes as a full form-encoded XHR identical to the theme's own
  // submission. It always hits the same URL the theme uses, so URL/body
  // shape cannot distinguish it; only the call stack can.
  function isMwsRogueCartAddCall(requestUrl) {
    const normalizedUrl = String(requestUrl || '');

    if (!normalizedUrl.includes('/cart/add')) {
      return false;
    }

    const stack = new Error().stack || '';

    return stack.includes('mws_upsell_bundle');
  }

  function fakeSuccessfulXhrResponse(xhr) {
    const fakeBody = JSON.stringify({ blocked: true, reason: 'duplicate_mws_cart_add' });

    ['readyState', 'status', 'statusText', 'responseText', 'response'].forEach((prop) => {
      try {
        Object.defineProperty(xhr, prop, {
          configurable: true,
          value: prop === 'readyState' ? 4 : prop === 'status' ? 200 : prop === 'statusText' ? 'OK' : fakeBody
        });
      } catch (error) {
        // Some browsers may not allow redefining certain XHR properties — safe to ignore.
      }
    });

    window.setTimeout(function () {
      xhr.dispatchEvent(new Event('readystatechange'));
      xhr.dispatchEvent(new Event('load'));
      xhr.dispatchEvent(new Event('loadend'));
    }, 0);
  }

  function installCartMutationRecheck() {
    if (!getWrapper()) {
      return;
    }

    if (window.__customCompCartMutationRecheckInstalled) {
      return;
    }

    window.__customCompCartMutationRecheckInstalled = true;

    const originalFetch = window.fetch;

    if (typeof originalFetch === 'function') {
      window.fetch = function () {
        const requestUrl = getRequestUrl(arguments[0]);
        const isCartMutation = isCartMutationUrl(requestUrl);

        if (!isCartMutation) {
          return originalFetch.apply(this, arguments);
        }
        const fetchOptions = arguments[1] || {};

debugLog('fetch.cartMutation', {
  count: ++customCompCartMutationCounter,
  url: requestUrl,
  body: fetchOptions.body,
  stack: new Error().stack
});

if (isMwsRogueCartAddCall(requestUrl)) {
  console.warn('[custom comp] blocked duplicate MWS cart/add call (fetch):', {
    url: requestUrl,
    body: fetchOptions.body
  });

  return Promise.resolve(
    new Response(JSON.stringify({
      blocked: true,
      reason: 'duplicate_mws_cart_add'
    }), {
      status: 200,
      headers: {
        'Content-Type': 'application/json'
      }
    })
  );
}

return originalFetch.apply(this, arguments).then(function (response) {
  // Do not invalidate shared cart here — theme updateCart already
  // force-fetches once; wiping cache causes a second /cart.js wave.
  scheduleFreeShippingNoticeRecheck(900);
  scheduleCustomCompRecommendationsRefresh(1800);
  return response;
});
      };
    }

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__customCompRequestUrl = url;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function (body) {
      const xhr = this;
      const requestUrl = xhr.__customCompRequestUrl;
      const isMutation = isCartMutationUrl(requestUrl);

      if (isMutation) {
        debugLog('xhr.cartMutation', {
          count: ++customCompCartMutationCounter,
          url: requestUrl,
          body,
          stack: new Error().stack
        });
      }

      if (isMutation && isMwsRogueCartAddCall(requestUrl)) {
        console.warn('[custom comp] blocked duplicate MWS cart/add call (xhr):', {
          url: requestUrl,
          body
        });

        fakeSuccessfulXhrResponse(xhr);
        return undefined;
      }

      xhr.addEventListener('loadend', function () {
if (isCartMutationUrl(xhr.__customCompRequestUrl)) {
  scheduleFreeShippingNoticeRecheck(900);
  scheduleCustomCompRecommendationsRefresh(1800);
}
      });

      return originalSend.apply(this, arguments);
    };

document.addEventListener('cart:updated', function () {
  scheduleFreeShippingNoticeRecheck(900);
  scheduleCustomCompRecommendationsRefresh(1800);
});
  }

  /* ----------------------------------------------------------------
   * Complementary product add-to-cart request
   * ---------------------------------------------------------------- */

  async function addComplementaryItems(items) {
    const payload = {
      items: items.map((item) => ({
        id: item.id,
        quantity: item.quantity
      }))
    };

    debugLog('addComplementaryItems.request', {
      payload,
      stack: new Error().stack
    });

    const response = await fetch(getShopifyRoute('cart/add.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      debugLog('addComplementaryItems.error', { status: response.status, errorText });
      throw new Error(errorText);
    }

    const json = await response.json();

    debugLog('addComplementaryItems.response', {
      items: (json.items || []).map((item) => ({
        id: item.variant_id,
        title: item.title,
        quantity: item.quantity
      }))
    });

    return json;
  }

  /* ----------------------------------------------------------------
   * Main Add to cart interception
   * ---------------------------------------------------------------- */

  document.addEventListener(
    'click',
    async function (event) {
const button = event.target.closest(
  '.product-page--submit-action button, .product-page--submit-action input[type="submit"]'
);

if (!button) return;

const form = button.closest('form');
if (!form) return;

      const isProductAddForm =
        form.matches('#add-to-cart-form') ||
        (form.getAttribute('action') || '').includes('/cart/add');

      if (!isProductAddForm) return;

      debugLog('click.intercepted', {
        resumeFlag: button.dataset.customCompResume,
        checkedToggles: Array.from(document.querySelectorAll(`${SELECTORS.toggle}:checked`))
          .map((t) => t.dataset.variantId),
        allToggles: Array.from(document.querySelectorAll(SELECTORS.toggle))
          .map((t) => ({ variantId: t.dataset.variantId, checked: t.checked })),
        alreadyAdded: Array.from(addedComplementaryVariantIds),
        isAddingComplementaryProducts
      });

      if (button.dataset.customCompResume === 'true') {
        delete button.dataset.customCompResume;
        debugLog('click.resume-skip', {});
        return;
      }

      const selectedComplementaryItems = getSelectedComplementaryItems();

      debugLog('click.selectedComplementaryItems', { selectedComplementaryItems });

      if (!selectedComplementaryItems.length) return;

      if (isAddingComplementaryProducts) {
        debugLog('click.blocked-reentrant', {});
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      isAddingComplementaryProducts = true;

      try {
        await addComplementaryItems(selectedComplementaryItems);
        markComplementaryItemsAsAdded(selectedComplementaryItems);

        debugLog('click.resume-native-submit', {
          buttonAttachedResumeFlag: true
        });

        button.dataset.customCompResume = 'true';
        button.click();
      } catch (error) {
        console.error('[custom comp] failed to add complementary products:', error);
        alert('Could not add the selected recommended product. Please try again.');
      } finally {
        isAddingComplementaryProducts = false;
        setNoticeVisible(false);
      }
    },
    true
  );

  /* ----------------------------------------------------------------
   * Product, quantity, and complementary toggle recalculation
   * ---------------------------------------------------------------- */

  document.addEventListener('change', function (event) {
    const target = event.target;

    if (
      target.matches(SELECTORS.toggle) ||
      target.matches('[name="id"]') ||
      target.matches('[name="quantity"]') ||
      target.matches('#add-to-cart-form .swatch input, #add-to-cart-form .wetheme-custom-radio') ||
      target.closest(SELECTORS.productForm)
    ) {
      window.setTimeout(function () {
        updateCustomCompBlockVisibility();
        updateFreeShippingNotice();
      }, 0);
    }
  });

  document.addEventListener('variantUnavailable', function () {
    updateCustomCompBlockVisibility();
    updateFreeShippingNotice();
  });

  document.addEventListener('click', function (event) {
    const target = event.target;

    if (
      target.closest &&
      target.closest('#add-to-cart-form .swatch-element, #add-to-cart-form .swatch label')
    ) {
      window.setTimeout(function () {
        updateCustomCompBlockVisibility();
        updateFreeShippingNotice();
      }, 50);
      window.setTimeout(function () {
        updateCustomCompBlockVisibility();
        updateFreeShippingNotice();
      }, 300);
    }
  });

  /* ----------------------------------------------------------------
   * Recharge widget recalculation
   * ---------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];

    const touchedRechargeWidget = path.some((node) => {
      if (!node || !node.classList) {
        return false;
      }

      return Array.from(node.classList).some((className) => {
        return className.indexOf('rc-') === 0;
      });
    });

    if (touchedRechargeWidget) {
      window.setTimeout(updateFreeShippingNotice, 100);
      window.setTimeout(updateFreeShippingNotice, 500);
    }
  });

  document.addEventListener('change', function (event) {
    const target = event.target;

    if (target.matches('[name="selling_plan"]')) {
      window.setTimeout(updateFreeShippingNotice, 100);
      window.setTimeout(updateFreeShippingNotice, 500);
    }
  });





  

  /* ----------------------------------------------------------------
   * Initialization
   * ---------------------------------------------------------------- */

function initCustomCompFreeShippingNotice() {
  updateCustomCompBlockVisibility();
  installCartMutationRecheck();
  installInitialRecommendedProductReadyRecheck();
  updateFreeShippingNotice();
}

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomCompFreeShippingNotice);
  } else {
    initCustomCompFreeShippingNotice();
  }
})();